// src/pages/tabs/ContextErvaringTab.jsx
import ChartCard from "../../components/ui/ChartCard";
import { CeoTextModalButton } from "../../components/ui/CeoTextAnswers";
import "../../components/sections/DashboardCharts.css";
import { buildRadarOptions, buildStackedBarOptions } from "./chartOptions";

function buildDataSource(data) {
  const source = data?.contextErvaringMetrics || {};

  return {
    ratings: Array.isArray(source.ratings) ? source.ratings : [],
    ervaringCounts: Array.isArray(source.ervaring_counts)
      ? source.ervaring_counts
      : [],
    gevoelensCounts: Array.isArray(source.gevoelens_counts)
      ? source.gevoelens_counts
      : [],
    agePyramid: source?.leeftijd_groepen?.series
      ? source.leeftijd_groepen
      : { labels: [], series: [] },
    ervaringOver: source?.ervaring_over?.series
      ? source.ervaring_over
      : { categories: [], series: [], series_rel: [] },
    teamCategoryCounts: source?.category_teams_counts?.series
      ? source.category_teams_counts
      : { categories: [], series: [], series_rel: [] },
  };
}

const GEVOELENS_ORDER = [
  "Dankbaar",
  "Gefrustreerd",
  "Hoopvol",
  "Bezorgd",
  "Blij",
  "Boos",
  "Opgelucht",
  "Teleurgesteld",
  "Anders, namelijk..",
];

function normalizeGevoelensLabel(label) {
  return String(label || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[.,/#!$%^&*;:{}=\-_`~()"'?<>[\]|\\]/g, "")
    .toLowerCase();
}

function hashKey(input) {
  const text = String(input || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

export default function ContextErvaringTab({ data = {}, mode = "abs", showTextAnswers = false }) {
  const textAnswers = data?.ceoTextAnswers || {};
  const {
    ratings,
    ervaringCounts,
    gevoelensCounts,
    agePyramid,
    ervaringOver,
    teamCategoryCounts,
  } = buildDataSource(data);

  // --- Gevoelens data (Radar Chart) ---
  const canonicalGevoelens = new Map(
    GEVOELENS_ORDER.map((label) => [normalizeGevoelensLabel(label), label])
  );
  const gevoelensLookup = new Map();
  const extraGevoelens = [];
  const seenExtra = new Set();

  gevoelensCounts.forEach((g) => {
    const rawLabel = String(g.label || "").trim();
    if (!rawLabel) {
      return;
    }
    const normalized = normalizeGevoelensLabel(rawLabel);
    const canonicalLabel = canonicalGevoelens.get(normalized) || rawLabel;
    const key = normalizeGevoelensLabel(canonicalLabel);
    const prev = gevoelensLookup.get(key) || {
      label: canonicalLabel,
      count: 0,
      count_rel: 0,
    };
    const next = {
      label: prev.label,
      count: (Number(prev.count) || 0) + (Number(g.count) || 0),
      count_rel: (Number(prev.count_rel) || 0) + (Number(g.count_rel) || 0),
    };
    gevoelensLookup.set(key, next);

    if (!canonicalGevoelens.has(normalized) && !seenExtra.has(key)) {
      extraGevoelens.push(canonicalLabel);
      seenExtra.add(key);
    }
  });

  const gevoelensLabels = [...GEVOELENS_ORDER, ...extraGevoelens];
  const gevoelensAbsValues = gevoelensLabels.map(
    (label) =>
      Number(gevoelensLookup.get(normalizeGevoelensLabel(label))?.count) || 0
  );
  const gevoelensRelValues = gevoelensLabels.map(
    (label) =>
      Number(gevoelensLookup.get(normalizeGevoelensLabel(label))?.count_rel) || 0
  );
  const gevoelensValues = mode === "rel" ? gevoelensRelValues : gevoelensAbsValues;
  const gevoelensChartKey = `gevoelens-${hashKey(
    `${mode}|${gevoelensLabels.join("|")}|${gevoelensValues.join(",")}`
  )}`;
  const gevoelensBaseline = mode === "rel" ? 10 : 50;
  const gevoelensPlotValues = gevoelensValues;

  const gevoelensOptions = buildRadarOptions({
    categories: gevoelensLabels,
    size: 120,
    yaxis: {
      min: -gevoelensBaseline,
      ...(mode === "rel" ? { max: 100, tickAmount: 5 } : {}),
    },
    tooltipFormatter: (_, opts) => {
      const idx = opts?.dataPointIndex ?? 0;
      const abs = gevoelensAbsValues[idx] ?? 0;
      const rel = gevoelensRelValues[idx] ?? 0;
      return `${abs} (${rel}%)`;
    },
    dataLabelsFormatter: (val) =>
      Number(val) === 0
        ? ""
        : mode === "rel"
          ? `${Number(val).toFixed(1)}%`
          : `${val}`,
  });
  gevoelensOptions.legend = { show: false };
  gevoelensOptions.grid = { padding: { top: 12, bottom: 20 } };

  gevoelensOptions.colors = ["#e5e7eb", "#0ea5e9"];
  gevoelensOptions.stroke = { width: [1, 2] };
  gevoelensOptions.fill = { opacity: [0, 0.2] };
  gevoelensOptions.markers = { size: [0, 4] };
  gevoelensOptions.dataLabels = {
    ...gevoelensOptions.dataLabels,
    formatter: (val, opts) => {
      if (opts?.seriesIndex === 0) {
        return "";
      }
      return mode === "rel" ? `${Number(val).toFixed(1)}%` : `${val}`;
    },
  };

  const gevoelensSeries = [
    {
      name: "Nulpunt",
      data: gevoelensLabels.map(() => 0),
    },
    { name: "Aantal antwoorden", data: gevoelensPlotValues },
  ];

  // --- Ervaring Likert data ---
  const ervaringLabels = ervaringCounts.map((e) => e.label);
  const ervaringAbsValues = ervaringCounts.map((e) => Number(e.count) || 0);
  const ervaringRelValues = ervaringCounts.map((e) => Number(e.count_rel) || 0);
  const ervaringValues = mode === "rel" ? ervaringRelValues : ervaringAbsValues;

  const ervaringOptions = {
    chart: { toolbar: { show: false } },
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4 },
    },
    xaxis: { categories: ervaringLabels },
    colors: [
      ({ value }) => {
        if (value === "Zeer positief") return "#22c55e";
        if (value === "Positief") return "#84cc16";
        if (value === "Neutraal") return "#eab308";
        if (value === "Negatief") return "#f97316";
        return "#ef4444";
      },
    ],
    dataLabels: {
      enabled: true,
      formatter: (val) =>
        mode === "rel" ? `${Number(val).toFixed(1)}%` : `${val}`,
    },
    tooltip: {
      y: {
        formatter: (_, opts) => {
          const idx = opts?.dataPointIndex ?? 0;
          const abs = ervaringAbsValues[idx] ?? 0;
          const rel = ervaringRelValues[idx] ?? 0;
          return `${abs} (${rel}%)`;
        },
      },
    },
  };

  const ervaringSeries = [{ name: "Aantal antwoorden", data: ervaringValues }];

  // --- Ratings ---
  const categories = ratings.map((r) => r.text);
  const scores = ratings.map((r) => r.score);

  const ratingsOptions = {
    chart: { toolbar: { show: false } },
    xaxis: { categories },
    plotOptions: {
      bar: { borderRadius: 4, distributed: true },
    },
    colors: [
      ({ value }) => {
        if (value < 3) return "#E9D5FF";
        if (value < 5) return "#C084FC";
        return "#7E22CE";
      },
    ],
  };

  const ratingsSeries = [{ name: "Scores", data: scores }];

  // --- Leeftijd groepen (Funnel/Pyramid) ---
  const ageFunnelLabels = agePyramid?.labels ?? [];
  const ageFunnelData =
    (agePyramid?.series?.[1]?.data ?? agePyramid?.series?.[0]?.data ?? []).map(
      (v) => Math.abs(v)
    );
  const ageRelValues = Array.isArray(agePyramid?.rel) ? agePyramid.rel : [];

  const pyramidOptions = {
    chart: { type: "bar", height: 350, toolbar: { show: false } },
    plotOptions: {
      bar: {
        borderRadius: 3,
        horizontal: true,
        distributed: true,
        barHeight: "100%",
        isFunnel: true,
      },
    },
    colors: [
      "#F44F5E",
      "#E55A89",
      "#D863B1",
      "#CA6CD8",
      "#B57BED",
      "#8D95EB",
      "#62ACEA",
      "#4BC3E6",
    ],
    dataLabels: {
      enabled: true,
      formatter: (_, opt) => opt.w.globals.labels[opt.dataPointIndex] || "",
      style: {
        fontSize: "12px",
        fontWeight: 600,
        lineHeight: "16px",
      },
      textAnchor: "middle",
      dropShadow: { enabled: false },
    },
    xaxis: { categories: ageFunnelLabels },
    legend: { show: false },
    tooltip: {
      y: {
        formatter: (_, opts) => {
          const idx = opts?.dataPointIndex ?? 0;
          const abs = ageFunnelData[idx] ?? 0;
          const rel = ageRelValues[idx] ?? 0;
          return mode === "rel" ? `${rel}%` : `${abs}`;
        },
      },
    },
  };

  const pyramidSeries = [{ name: "", data: ageFunnelData }];

  // --- Ervaring over (7 categories, stacked column) ---
  const ervaringOverCategories = ervaringOver?.categories ?? [];
  const ervaringOverAbsSeries = ervaringOver?.series ?? [];
  const ervaringOverRelSeries = ervaringOver?.series_rel ?? [];
  const ervaringOverSeries = mode === "rel" ? ervaringOverRelSeries : ervaringOverAbsSeries;

  const truncateLabel = (label, maxLength = 32) => {
    if (!label) {
      return "";
    }
    const text = String(label);
    if (text.length <= maxLength) {
      return text;
    }
    return `${text.slice(0, Math.max(1, maxLength - 3))}...`;
  };

  const ervaringOverOptions = buildStackedBarOptions({
    categories: ervaringOverCategories,
    colors: ["#16a34a", "#84cc16", "#eab308", "#f97316", "#ef4444"],
    columnWidth: "55%",
    fillOpacity: 0.9,
    stackType: mode === "rel" ? "100%" : "normal",
    tooltipFormatter: (_, opts) => {
      const sIdx = opts?.seriesIndex ?? 0;
      const dIdx = opts?.dataPointIndex ?? 0;
      const abs = ervaringOverAbsSeries?.[sIdx]?.data?.[dIdx] ?? 0;
      const rel = ervaringOverRelSeries?.[sIdx]?.data?.[dIdx] ?? 0;
      return `${abs} (${rel}%)`;
    },
  });
  ervaringOverOptions.fullscreenGridPaddingBottom = 96;
  ervaringOverOptions.xaxis = {
    ...(ervaringOverOptions.xaxis || {}),
    labels: {
      ...(ervaringOverOptions.xaxis?.labels || {}),
      formatter: (val) => truncateLabel(val, 32),
    },
  };
  ervaringOverOptions.fullscreenOverrides = {
    grid: {
      padding: {
        left: 28,
        right: 16,
        top: 8,
        bottom: 64,
      },
    },
    legend: {
      position: "bottom",
      offsetY: 12,
    },
    xaxis: {
      labels: {
        rotate: -38,
        rotateAlways: true,
        trim: false,
        minHeight: 110,
        maxHeight: 170,
        formatter: (val) => (val == null ? "" : String(val)),
      },
    },
  };

  // --- Category teams count occurrence (stacked bar) ---
  const teamCategories = teamCategoryCounts?.categories ?? [];
  const teamAbsSeries = teamCategoryCounts?.series ?? [];
  const teamRelSeries = teamCategoryCounts?.series_rel ?? [];
  const teamSeries = mode === "rel" ? teamRelSeries : teamAbsSeries;

  const teamStackedOptions = buildStackedBarOptions({
    categories: teamCategories,
    colors: ["#7E22CE", "#3B82F6", "#22c55e", "#f97316", "#ef4444"],
    horizontal: true,
    fillOpacity: 0.9,
    stackType: mode === "rel" ? "100%" : "normal",
    tooltipFormatter: (_, opts) => {
      const sIdx = opts?.seriesIndex ?? 0;
      const dIdx = opts?.dataPointIndex ?? 0;
      const abs = teamAbsSeries?.[sIdx]?.data?.[dIdx] ?? 0;
      const rel = teamRelSeries?.[sIdx]?.data?.[dIdx] ?? 0;
      return `${abs} (${rel}%)`;
    },
  });
  teamStackedOptions.xaxis = {
    ...(teamStackedOptions.xaxis || {}),
    labels: {
      ...(teamStackedOptions.xaxis?.labels || {}),
      trim: false,
      maxHeight: 200,
    },
  };
  teamStackedOptions.fullscreenOverrides = {
    grid: {
      padding: {
        left: 180,
        right: 16,
        top: 12,
        bottom: 24,
      },
    },
    xaxis: {
      labels: {
        trim: false,
        maxHeight: 200,
      },
    },
  };

  return (
    <div>
      <div className="row row-split">
        <div className="col-1-3">
          <ChartCard
            title="Ervaring Likert Scores"
            containerClassName="ceo-text-chart"
            options={ervaringOptions}
            series={ervaringSeries}
            type="bar"
            info="Hoe was jouw ervaring?"
            headerActions={showTextAnswers ? <CeoTextModalButton title="Mijn ervaring" items={textAnswers.mijn_ervaring || []} /> : null}
          />
        </div>
        <div className="col-2-3">
          <ChartCard
            title="Gevoelens bij je verhaal"
            containerClassName="ceo-text-chart"
            options={gevoelensOptions}
            series={gevoelensSeries}
            type="radar"
            chartKey={gevoelensChartKey}
            info="Welke emoties had je bij je ervaring? (maximaal 3 antwoorden)"
            headerActions={showTextAnswers ? <CeoTextModalButton title="Anders, namelijk: gevoelens bij je verhaal" items={textAnswers.gevoelens_anders || []} /> : null}
          />
        </div>

      </div>

      <div className="row row-3">
        <ChartCard
          title="Leeftijdsgroepen (Piramide)"
          options={pyramidOptions}
          series={pyramidSeries}
          type="bar"
          info="Hoe oud was je (kind) toen je deze ervaring had?"
        />
        <ChartCard
          title="Ervaring over (per onderwerp)"
          containerClassName="ceo-text-chart"
          options={ervaringOverOptions}
          series={ervaringOverSeries}
          type="bar"
          info="De ervaring die je hebt verteld gaat vooral over….. (maximaal 3 antwoorden)"
          headerActions={showTextAnswers ? <CeoTextModalButton title="Anders, namelijk: ervaring over" items={textAnswers.ervaring_over_anders || []} /> : null}
        />
        <ChartCard
          title="Teams per categorie (stacks)"
          options={teamStackedOptions}
          series={teamSeries}
          type="bar"
          info="Ik ben een jongere of ouder/verzorger"
        />
      </div>
    </div>
  );
}
